import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import { AiOutlineArrowLeft } from "react-icons/ai";
import { BsFillBagFill } from "react-icons/bs";
import { BiMessageSquareDetail } from "react-icons/bi";
import { backend_url, server } from "../../server";
import { getAllOrdersOfShop } from "../../redux/actions/order";
import MobileFriendlyModal, { MODAL_INPUT_CLASS } from "../Layout/MobileFriendlyModal";

// Currency formatter for GBP
const formatCurrency = (value) => {
    if (value === null || value === undefined) return "£0.00";
    return new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: "GBP",
    }).format(Number(value));
};

/** Stored paths from upload may be relative; some records may already hold absolute URLs. */
const refundEvidenceSrc = (img) => {
  const path = typeof img === "string" ? img.trim() : "";
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  return `${backend_url}${path.replace(/^\//, "")}`;
};

const RefundEvidenceGallery = ({ images, heading = "Buyer evidence (photos)" }) => {
  const urls = (Array.isArray(images) ? images : []).map(refundEvidenceSrc).filter(Boolean);
  if (!urls.length) return null;
  return (
    <div className="mt-3">
      <p className="text-xs font-medium text-slate-700 mb-2">{heading}</p>
      <div className="flex flex-wrap gap-2">
        {urls.map((src, idx) => (
          <a
            key={`${src}-${idx}`}
            href={src}
            target="_blank"
            rel="noopener noreferrer"
            className="block overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm hover:opacity-90"
          >
            <img
              src={src}
              alt={`Refund evidence ${idx + 1}`}
              className="h-24 w-24 object-cover"
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          </a>
        ))}
      </div>
    </div>
  );
};

const RefundDetails = ({ orderIdFromQuery, embedded, onBackToList }) => {
  const { id: paramId } = useParams();
  const id = orderIdFromQuery ?? paramId;
  const navigate = useNavigate();
  const backToListUrl = "/profile/disputes-refunds?tab=review-refunds";
  const BackLink = embedded && onBackToList
    ? () => (
        <button
          type="button"
          onClick={onBackToList}
          className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 transition"
        >
          <AiOutlineArrowLeft size={20} />
        </button>
      )
    : () => (
        <Link
          to={backToListUrl}
          className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 transition"
        >
          <AiOutlineArrowLeft size={20} />
        </Link>
      );
  const dispatch = useDispatch();
  const { shopOrders, isLoading } = useSelector((state) => state.order);
  const { user } = useSelector((state) => state.user);
  const [isProcessing, setIsProcessing] = useState(false);
  const [singleOrder, setSingleOrder] = useState(null);
  const [singleOrderLoading, setSingleOrderLoading] = useState(false);

  const sellerId = user?._id;

  useEffect(() => {
    if (sellerId && user?.isSeller) {
      dispatch(getAllOrdersOfShop(sellerId));
    }
  }, [dispatch, sellerId, user?.isSeller]);

  useEffect(() => {
    const fetchSingleOrder = async () => {
      if (!id || !sellerId || !user?.isSeller) return;
      setSingleOrderLoading(true);
      try {
        const { data } = await axios.get(`${server}/order/seller-order/${id}`, {
          withCredentials: true,
        });
        if (data.success && data.order) setSingleOrder(data.order);
        else setSingleOrder(null);
      } catch {
        setSingleOrder(null);
      } finally {
        setSingleOrderLoading(false);
      }
    };
    if (id && sellerId && user?.isSeller && !isLoading && !shopOrders?.find((o) => o._id === id)) {
      fetchSingleOrder();
    } else if (shopOrders?.find((o) => o._id === id)) {
      setSingleOrder(null);
    }
  }, [id, sellerId, user?.isSeller, isLoading, shopOrders]);

  const orderFromList = useMemo(() => shopOrders?.find((item) => item._id === id), [shopOrders, id]);
  const order = orderFromList || singleOrder;

  // Effective status: when all product refunds are rejected/success/resolved, use that so UI stays consistent with list
  const effectiveOrderStatus = useMemo(() => {
    if (!order?.refunds?.length) return order?.status;
    const allRejected = order.refunds.every((r) => r.status === "Refund Rejected");
    const allSuccess = order.refunds.every((r) => r.status === "Refund Success");
    const allResolved = order.refunds.every((r) => r.status === "Refund Resolved");
    const hasAwaitingSellerConfirmation = order.refunds.some(
      (r) => r.status === "Refund Awaiting Seller Confirmation"
    );
    const hasReturnInTransit = order.refunds.some((r) => r.status === "Return in Transit");
    const hasReturnRequired = order.refunds.some((r) => r.status === "Return Required");
    const hasOfferPending = order.refunds.some((r) => r.status === "Refund Offer Pending");
    if (allRejected) return "Refund Rejected";
    if (allSuccess) return "Refund Success";
    if (allResolved) return "Refund Resolved";
    if (hasOfferPending) return "Refund Offer Pending";
    if (hasAwaitingSellerConfirmation) return "Refund Awaiting Seller Confirmation";
    if (hasReturnInTransit) return "Return in Transit";
    if (hasReturnRequired) return "Return Required";
    return order.status;
  }, [order?.status, order?.refunds]);

  const hasPendingRefundActions = useMemo(
    () => order?.refunds?.some((r) => r.status === "Processing refund") ?? false,
    [order?.refunds]
  );
  const hasLegacyPendingRefundAction = useMemo(
    () =>
      effectiveOrderStatus === "Processing refund" &&
      (!order?.refunds || order.refunds.length === 0),
    [effectiveOrderStatus, order?.refunds]
  );

  const orderItems = order?.cart || [];
  const totalQuantity = orderItems.reduce((acc, item) => acc + item.qty, 0);

  // Open or create conversation with buyer (reuses existing chat – no duplicate)
  const openChatWithBuyer = useCallback(async () => {
    const buyerId = order?.user?._id ?? order?.user;
    if (!buyerId || !sellerId) {
      toast.error("Buyer information not available");
      return;
    }
    try {
      const groupTitle = `${sellerId}_${buyerId}`;
      const res = await axios.post(
        `${server}/conversation/create-new-conversation`,
        { groupTitle, userId: buyerId, sellerId },
        { withCredentials: true }
      );
      const conversationId =
        res.data?.existingConversation?._id ||
        res.data?.conversation?._id ||
        res.data?._id;
      if (conversationId) {
        navigate(`/profile/inbox?conversation=${conversationId}`);
      } else {
        toast.error("Unable to open conversation");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Unable to open chat.");
    }
  }, [order?.user, sellerId, navigate]);

  const [partialModal, setPartialModal] = useState({ open: false, refundId: null, amount: "" });

  const handleRefundAction = async (status, refundId = null, rejectionReason = "", extra = {}) => {
    if (!order) return;

    try {
      setIsProcessing(true);
      await axios.put(
        `${server}/order/order-refund-success/${order._id}`,
        {
          status,
          refundId,
          rejectionReason: status === "Refund Rejected" ? rejectionReason : undefined,
          sellerSettlement: extra.sellerSettlement,
          partialAmount: extra.partialAmount,
        },
        { withCredentials: true }
      );
      const settlement = extra.sellerSettlement;
      toast.success(
        status === "Refund Resolved"
          ? "Refund request resolved (no refund processed)."
          : status === "Refund Rejected"
          ? "Refund rejected successfully."
          : settlement === "wallet_partial_now"
          ? "Partial refund offer sent to the buyer."
          : "Refund decision saved."
      );
      setPartialModal({ open: false, refundId: null, amount: "" });
      await dispatch(getAllOrdersOfShop(sellerId)); // refetch so order.status and list stay in sync
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to process refund");
    } finally {
      setIsProcessing(false);
    }
  };

  if ((isLoading || singleOrderLoading) && !order) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#38513b] border-r-transparent"></div>
          <p className="mt-4 text-slate-600">Loading refund details...</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-slate-900 mb-2">Refund not found</h2>
          <p className="text-slate-600 mb-6">The refund request you're looking for doesn't exist.</p>
          {embedded && onBackToList ? (
            <button
              type="button"
              onClick={onBackToList}
              className="inline-flex items-center gap-2 rounded-xl bg-[#38513b] px-6 py-2.5 text-sm font-semibold text-white hover:bg-[#2f4232] transition"
            >
              <AiOutlineArrowLeft size={18} />
              Back to Disputes and Refunds
            </button>
          ) : (
            <Link
              to={backToListUrl}
              className="inline-flex items-center gap-2 rounded-xl bg-[#38513b] px-6 py-2.5 text-sm font-semibold text-white hover:bg-[#2f4232] transition"
            >
              <AiOutlineArrowLeft size={18} />
              Back to Disputes and Refunds
            </Link>
          )}
        </div>
      </div>
    );
  }

  if (
    effectiveOrderStatus !== "Processing refund" &&
    effectiveOrderStatus !== "Return Required" &&
    effectiveOrderStatus !== "Return in Transit" &&
    effectiveOrderStatus !== "Refund Awaiting Seller Confirmation" &&
    effectiveOrderStatus !== "Refund Offer Pending" &&
    effectiveOrderStatus !== "Refund Success" &&
    effectiveOrderStatus !== "Refund Rejected" &&
    effectiveOrderStatus !== "Refund Resolved"
  ) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-slate-900 mb-2">Not a refund request</h2>
          <p className="text-slate-600 mb-6">This order is not a refund request.</p>
          {embedded && onBackToList ? (
            <button
              type="button"
              onClick={onBackToList}
              className="inline-flex items-center gap-2 rounded-xl bg-[#38513b] px-6 py-2.5 text-sm font-semibold text-white hover:bg-[#2f4232] transition"
            >
              <AiOutlineArrowLeft size={18} />
              Back to Disputes and Refunds
            </button>
          ) : (
            <Link
              to={backToListUrl}
              className="inline-flex items-center gap-2 rounded-xl bg-[#38513b] px-6 py-2.5 text-sm font-semibold text-white hover:bg-[#2f4232] transition"
            >
              <AiOutlineArrowLeft size={18} />
              Back to Disputes and Refunds
            </Link>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[70vh] py-6 sm:py-10">
      <div className="mx-auto max-w-5xl px-4">
        <header className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex min-w-0 w-full items-start gap-3 sm:flex-1">
            <BackLink />
            <div className="min-w-0 flex-1">
              <h1 className="text-xl font-semibold text-slate-900 sm:text-2xl">
                Dispute/Refund Request Details
              </h1>
              <p className="text-sm text-slate-500">
                Order ID: <span className="font-mono">{order?.orderNumber ?? order?._id?.toString().substring(0, 8) ?? "N/A"}</span>
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={openChatWithBuyer}
            className="inline-flex h-11 w-full shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-[#38513b] hover:text-[#38513b] sm:mt-0 sm:w-auto sm:self-center"
          >
            <BiMessageSquareDetail size={20} className="shrink-0" />
            Chat with buyer
          </button>
        </header>

        <div className="space-y-6">
          {/* Refund Status & Reason */}
          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Dispute/Refund Information</h2>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-slate-600 mb-1">Status</p>
                <span
                  className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
                    effectiveOrderStatus === "Refund Success"
                      ? "bg-green-100 text-green-700"
                      : effectiveOrderStatus === "Refund Rejected"
                      ? "bg-red-100 text-red-700"
                      : effectiveOrderStatus === "Refund Resolved"
                      ? "bg-indigo-100 text-indigo-700"
                      : effectiveOrderStatus === "Refund Offer Pending"
                      ? "bg-teal-100 text-teal-800"
                      : effectiveOrderStatus === "Return Required" || effectiveOrderStatus === "Return in Transit"
                      || effectiveOrderStatus === "Refund Awaiting Seller Confirmation"
                      ? "bg-amber-100 text-amber-700"
                      : "bg-yellow-100 text-yellow-700"
                  }`}
                >
                  {effectiveOrderStatus === "Refund Success"
                    ? "Refund credited"
                    : effectiveOrderStatus === "Refund Rejected"
                    ? "Declined"
                    : effectiveOrderStatus === "Refund Resolved"
                    ? "Resolved (no refund)"
                    : effectiveOrderStatus === "Return Required"
                    ? "Return required"
                    : effectiveOrderStatus === "Return in Transit"
                    ? "Return in transit"
                    : effectiveOrderStatus === "Refund Awaiting Seller Confirmation"
                    ? "Awaiting seller confirmation"
                    : "Under review"}
                </span>
              </div>
              <div>
                <p className="text-sm text-slate-600 mb-1">Refund Requested At</p>
                <p className="font-semibold text-slate-900">
                  {order.refundRequestedAt
                    ? new Date(order.refundRequestedAt).toLocaleString()
                    : order.createdAt
                    ? new Date(order.createdAt).toLocaleString()
                    : "N/A"}
                </p>
              </div>
              
              {/* Product-specific refunds */}
              {order.refunds && order.refunds.length > 0 && (
                <div>
                  <p className="text-sm text-slate-600 mb-2 font-medium">Product-Specific Refunds</p>
                  <div className="space-y-3">
                    {order.refunds.map((refund, idx) => (
                      <div key={idx} className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <p className="text-sm font-semibold text-slate-900">{refund.productName}</p>
                            <p className="text-xs text-slate-600 mt-1">
                              Quantity: {refund.quantity} • Refund Amount: {formatCurrency(refund.refundAmount)}
                            </p>
                            <span
                              className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold mt-2 ${
                                refund.status === "Refund Success"
                                  ? "bg-green-100 text-green-700"
                                  : refund.status === "Refund Rejected"
                                  ? "bg-red-100 text-red-700"
                                  : refund.status === "Refund Resolved"
                                  ? "bg-indigo-100 text-indigo-700"
                                  : refund.status === "Return Required" || refund.status === "Return in Transit"
                                  || refund.status === "Refund Awaiting Seller Confirmation"
                                  ? "bg-amber-100 text-amber-700"
                                  : "bg-yellow-100 text-yellow-700"
                              }`}
                            >
                              {refund.status === "Refund Success"
                                ? "Refund credited"
                                : refund.status === "Refund Rejected"
                                ? "Declined"
                                : refund.status === "Refund Resolved"
                                ? "Resolved (no refund)"
                                : refund.status === "Return Required"
                                ? "Return required"
                                : refund.status === "Return in Transit"
                                ? "Return in transit"
                                : refund.status === "Refund Awaiting Seller Confirmation"
                                ? "Awaiting seller confirmation"
                                : "Under review"}
                            </span>
                          </div>
                        </div>
                        {(refund.reasonCategory || refund.reason) && (
                        <div className="mt-2">
                          {refund.reasonCategory && (
                            <p className="text-xs font-medium text-slate-700 mb-0.5">Category: {refund.reasonCategory}</p>
                          )}
                          <p className="text-xs font-medium text-slate-700 mb-1">Customer message:</p>
                          <p className="text-xs text-slate-900 whitespace-pre-wrap">{refund.reason}</p>
                        </div>
                        )}
                        {refund.status === "Refund Rejected" && (refund.rejectionReason || "").trim() && (
                          <div className="mt-2 p-2 rounded bg-red-50 border border-red-100">
                            <p className="text-xs font-medium text-red-800 mb-1">Seller reason for declining:</p>
                            <p className="text-xs text-slate-900 whitespace-pre-wrap">{refund.rejectionReason}</p>
                          </div>
                        )}
                        <RefundEvidenceGallery images={refund.images} heading="Evidence images" />
                        {(refund.returnTrackingCode || "").trim() && (
                          <div className="mt-3 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900">
                            <p className="font-semibold text-sky-950">Buyer return shipment</p>
                            <p className="mt-0.5">
                              {(refund.returnTrackingCourier || "").trim() || "Courier"} ·{" "}
                              <span className="font-mono">{refund.returnTrackingCode}</span>
                            </p>
                            {(refund.returnTrackingLink || "").trim() ? (
                              <a
                                href={
                                  String(refund.returnTrackingLink).startsWith("http")
                                    ? refund.returnTrackingLink
                                    : `https://${refund.returnTrackingLink}`
                                }
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-1 inline-block font-medium text-sky-800 underline"
                              >
                                Tracking link
                              </a>
                            ) : null}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Legacy whole-order refund reason + images (buyer uploads live on order.refundImages) */}
              {(String(order.refundReason || "").trim() ||
                (Array.isArray(order.refundImages) && order.refundImages.length > 0)) &&
                (!order.refunds || order.refunds.length === 0) && (
                <div>
                  <p className="text-sm text-slate-600 mb-2">Customer's Reason for Refund</p>
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                    {String(order.refundReason || "").trim() ? (
                      <p className="text-sm text-slate-900 whitespace-pre-wrap">
                        {order.refundReason}
                      </p>
                    ) : (
                      <p className="text-sm text-slate-500 italic">No written reason provided.</p>
                    )}
                    <RefundEvidenceGallery images={order.refundImages} />
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* Order Items */}
          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Order Items</h2>
            <div className="space-y-4">
              {orderItems.map((item, index) => {
                const productRefund = order?.refunds?.find(r => String(r.productId) === String(item._id));
                return (
                  <div
                    key={index}
                    className={`flex items-start gap-4 border-b border-slate-100 pb-4 last:border-0 last:pb-0 ${
                      productRefund ? 'bg-amber-50 p-3 rounded-lg border-amber-200' : ''
                    }`}
                  >
                    <img
                      src={
                        item.images && item.images.length > 0
                          ? item.images[0]?.url
                            ? `${backend_url}${item.images[0].url}`
                            : `${backend_url}${item.images[0]}`
                          : item.image
                            ? `${backend_url}${item.image}`
                            : "/placeholder-image.png"
                      }
                      alt={item.name || "Product"}
                      className="w-20 h-20 object-contain rounded-lg border border-slate-200"
                      onError={(e) => {
                        e.target.src = "/placeholder-image.png";
                      }}
                    />
                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold text-slate-900">{item.name}</h3>
                          <p className="text-sm text-slate-600 mt-1">
                            Quantity: {item.qty} × {formatCurrency(item.discountPrice || 0)}
                          </p>
                          <p className="text-sm font-semibold text-[#38513b] mt-2">
                            {formatCurrency((item.qty || 0) * (item.discountPrice || 0))}
                          </p>
                        </div>
                        {productRefund && (
                          <span
                            className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold ${
                              productRefund.status === "Refund Success"
                                ? "bg-green-100 text-green-700"
                                : productRefund.status === "Refund Rejected"
                                ? "bg-red-100 text-red-700"
                                : "bg-yellow-100 text-yellow-700"
                            }`}
                          >
                            {productRefund.status}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Customer Information */}
          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Customer Information</h2>
            <div className="space-y-2 text-sm">
              <p>
                <span className="font-medium text-slate-700">Name:</span>{" "}
                <span className="text-slate-900">{order.user?.name || "N/A"}</span>
              </p>
              <p>
                <span className="font-medium text-slate-700">Email:</span>{" "}
                <span className="text-slate-900">{order.user?.email || "N/A"}</span>
              </p>
              {order.user?.phoneNumber && (
                <p>
                  <span className="font-medium text-slate-700">Phone:</span>{" "}
                  <span className="text-slate-900">{order.user.phoneNumber}</span>
                </p>
              )}
            </div>
          </section>

          {/* Order Summary - use order's stored totals so breakdown matches totalPrice */}
          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Order Summary</h2>
            <div className="space-y-3">
              {(() => {
                const subtotal = Number(order?.subTotalPrice) ?? order?.cart?.reduce((acc, item) => acc + (item.qty || 0) * (item.discountPrice || 0), 0) ?? 0;
                const shipping = Number(order?.shipping) ?? 0;
                const discount = Number(order?.discountPrice) ?? 0;
                const buyerFee = Number(order?.buyerProtectionFee) ?? 0;
                return (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Subtotal</span>
                      <span className="font-semibold text-slate-900">{formatCurrency(subtotal)}</span>
                    </div>
                    {shipping > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Postage / shipping</span>
                        <span className="font-semibold text-slate-900">{formatCurrency(shipping)}</span>
                      </div>
                    )}
                    {discount > 0 && (
                      <div className="flex justify-between text-sm text-green-600">
                        <span>Discount</span>
                        <span className="font-semibold">-{formatCurrency(discount)}</span>
                      </div>
                    )}
                    {buyerFee > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Buyer protection</span>
                        <span className="font-semibold text-slate-900">{formatCurrency(buyerFee)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm pt-2 border-t border-slate-200">
                      <span className="text-slate-700 font-medium">Total paid</span>
                      <span className="font-semibold text-slate-900">{formatCurrency(order?.totalPrice ?? 0)}</span>
                    </div>
                  </>
                );
              })()}
              {order.refunds && order.refunds.length > 0 ? (
                (() => {
                  const creditedTotal = order.refunds
                    .filter((r) => r.status === "Refund Success")
                    .reduce((sum, r) => sum + Number(r.refundAmount || 0), 0);
                  const pendingOffers = order.refunds.filter(
                    (r) => r.status === "Refund Offer Pending"
                  );
                  return (
                    <>
                      {order.refunds.map((refund, idx) => (
                        <div key={idx} className="flex justify-between text-sm pt-2 border-t border-slate-200">
                          <span className="text-slate-600">
                            {refund.productName} (Qty: {refund.quantity})
                            {refund.status === "Refund Offer Pending" ? " · offer pending" : ""}
                            {refund.partialRefundAccepted ? " · partial accepted" : ""}
                          </span>
                          <span className="font-semibold text-slate-900">
                            {formatCurrency(refund.refundAmount)}
                          </span>
                        </div>
                      ))}
                      {Number(order.partialRefundAcceptedAmount) > 0 ? (
                        <div className="flex justify-between text-sm pt-2 text-emerald-700">
                          <span>Partial refund accepted (reporting)</span>
                          <span className="font-semibold">
                            {formatCurrency(order.partialRefundAcceptedAmount)}
                          </span>
                        </div>
                      ) : null}
                      {pendingOffers.length > 0 ? (
                        <div className="flex justify-between pt-3 border-t border-dashed border-teal-200 text-sm text-teal-800">
                          <span>Awaiting buyer response</span>
                          <span className="font-semibold">
                            {formatCurrency(
                              pendingOffers.reduce(
                                (s, r) => s + Number(r.sellerOfferAmount ?? r.refundAmount ?? 0),
                                0
                              )
                            )}
                          </span>
                        </div>
                      ) : null}
                      <div className="flex justify-between pt-3 border-t-2 border-slate-300">
                        <span className="text-lg font-semibold text-slate-900">
                          {creditedTotal > 0 ? "Total refunded to buyer" : "Total Refund Amount"}
                        </span>
                        <span className="text-lg font-bold text-[#38513b]">
                          {formatCurrency(creditedTotal > 0 ? creditedTotal : 0)}
                        </span>
                      </div>
                    </>
                  );
                })()
              ) : (
                <div className="flex justify-between pt-3 border-t-2 border-slate-200 mt-2">
                  <span className="text-lg font-semibold text-slate-900">Total Refund Amount</span>
                  <span className="text-lg font-bold text-[#38513b]">{formatCurrency(order?.totalPrice ?? 0)}</span>
                </div>
              )}
            </div>
          </section>

          {/* Refund Actions - only when there are pending items for seller to action */}
          {effectiveOrderStatus === "Processing refund" &&
            (hasPendingRefundActions || hasLegacyPendingRefundAction) && (
            <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Refund Actions</h2>
              <p className="text-sm text-slate-600 mb-4">
                <Link
                  to="/terms-conditions"
                  className="font-semibold text-[#38513b] underline underline-offset-2 hover:text-[#2f4232]"
                >
                  View our refund policy in the Terms &amp; Conditions
                </Link>
              </p>
              {order.refunds && order.refunds.length > 0 ? (
                <div className="space-y-3">
                  {order.refunds
                    .filter(r => r.status === "Processing refund")
                    .map((refund, idx) => {
                      const lineMax = Number(refund.lineMaxRefundAmount ?? refund.refundAmount ?? 0);
                      const kind = refund.buyerDisputeKind === "return" ? "return" : "refund";
                      return (
                      <div key={idx} className="p-4 border border-slate-200 rounded-lg bg-slate-50">
                        <p className="text-sm font-semibold text-slate-900 mb-1">{refund.productName}</p>
                        <p className="text-xs text-slate-600 mb-3">
                          Buyer request:{" "}
                          <span className="font-medium text-slate-800">
                            {kind === "return" ? "Return item for refund" : "Refund (money back)"}
                          </span>
                          {" · "}
                          Line max (item value): {formatCurrency(lineMax)}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() =>
                              handleRefundAction("Refund Success", refund._id, "", {
                                sellerSettlement: "return_required",
                              })
                            }
                            disabled={isProcessing}
                            className="inline-flex h-10 items-center justify-center rounded-xl bg-[#38513b] px-3 text-xs font-semibold text-white shadow-sm transition hover:bg-[#2f4232] disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {isProcessing ? "…" : "Refund after return"}
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              handleRefundAction("Refund Success", refund._id, "", {
                                sellerSettlement: "wallet_full_now",
                              })
                            }
                            disabled={isProcessing}
                            className="inline-flex h-10 items-center justify-center rounded-xl bg-emerald-600 px-3 text-xs font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Full refund now
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              setPartialModal({ open: true, refundId: refund._id, amount: String(lineMax) })
                            }
                            disabled={isProcessing}
                            className="inline-flex h-10 items-center justify-center rounded-xl bg-teal-600 px-3 text-xs font-semibold text-white shadow-sm transition hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Send partial refund offer
                          </button>
                        </div>
                      </div>
                    );
                    })}
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      handleRefundAction("Refund Success", null, "", { sellerSettlement: "return_required" })
                    }
                    disabled={isProcessing}
                    className="inline-flex h-11 items-center justify-center rounded-xl bg-[#38513b] px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-[#2f4232] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isProcessing ? "Processing..." : "Refund after return"}
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      handleRefundAction("Refund Success", null, "", { sellerSettlement: "wallet_full_now" })
                    }
                    disabled={isProcessing}
                    className="inline-flex h-11 items-center justify-center rounded-xl bg-emerald-600 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Full refund now
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const legacyMax = Number(order?.subTotalPrice ?? order?.totalPrice ?? 0);
                      setPartialModal({
                        open: true,
                        refundId: null,
                        amount: String(legacyMax > 0 ? legacyMax : ""),
                      });
                    }}
                    disabled={isProcessing}
                    className="inline-flex h-11 items-center justify-center rounded-xl bg-teal-600 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Send partial refund offer
                  </button>
                </div>
              )}
              <div className="flex flex-wrap gap-3 mt-3">
                <button
                  type="button"
                  onClick={openChatWithBuyer}
                  className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-6 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-[#38513b] hover:text-[#38513b]"
                >
                  <BiMessageSquareDetail size={18} />
                  Contact Buyer via Inbox
                </button>
                <Link
                  to={`/profile/seller-order/${order._id}`}
                  className="inline-flex h-11 items-center justify-center rounded-xl border border-slate-200 bg-white px-6 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-[#38513b] hover:text-[#38513b]"
                >
                  View Full Order Details
                </Link>
              </div>
            </section>
          )}
          {effectiveOrderStatus === "Refund Offer Pending" && (
            <section className="rounded-2xl border border-teal-200 bg-teal-50/60 p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-teal-950 mb-2">Awaiting buyer response</h2>
              <p className="text-sm text-teal-900 mb-4">
                Your partial refund offer has been sent. The buyer must accept before funds are credited to their wallet.
                If they decline, you can send another offer or continue the dispute in inbox.
              </p>
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={openChatWithBuyer}
                  className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-teal-200 bg-white px-6 text-sm font-semibold text-teal-900 shadow-sm transition hover:border-[#38513b]"
                >
                  <BiMessageSquareDetail size={18} />
                  Contact Buyer via Inbox
                </button>
                <Link
                  to={`/profile/seller-order/${order._id}`}
                  className="inline-flex h-11 items-center justify-center rounded-xl border border-teal-200 bg-white px-6 text-sm font-semibold text-teal-900 shadow-sm transition hover:border-[#38513b]"
                >
                  View Full Order Details
                </Link>
              </div>
            </section>
          )}
          {/* When refund is already resolved, still show contact / view order */}
          {(effectiveOrderStatus === "Refund Success" || effectiveOrderStatus === "Refund Rejected" || effectiveOrderStatus === "Refund Resolved" || effectiveOrderStatus === "Return Required" || effectiveOrderStatus === "Return in Transit" || effectiveOrderStatus === "Refund Awaiting Seller Confirmation") && (
            <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Refund request closed</h2>
              <p className="text-sm text-slate-600 mb-4">
                {effectiveOrderStatus === "Refund Success"
                  ? "Refund has been credited to buyer wallet. You can still contact the buyer or view the order."
                  : effectiveOrderStatus === "Refund Rejected"
                  ? "This refund request was declined. You can contact the buyer or view the order if needed."
                  : effectiveOrderStatus === "Return Required"
                  ? "Refund was approved and buyer needs to return the item."
                  : effectiveOrderStatus === "Return in Transit"
                  ? "Buyer marked return in transit. Confirm receipt to release the agreed wallet refund."
                  : effectiveOrderStatus === "Refund Awaiting Seller Confirmation"
                  ? "Return has been received. The refund is awaiting seller confirmation."
                  : "This refund request was resolved without a refund (e.g. customer changed mind or reached an agreement). You can contact the buyer or view the order if needed."}
              </p>
              <div className="flex flex-wrap gap-3">
                {effectiveOrderStatus === "Return in Transit" && (
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        const transitRefund = order?.refunds?.find((r) => r.status === "Return in Transit");
                        await axios.put(
                          `${server}/order/refund-return-received/${order._id}`,
                          transitRefund ? { refundId: transitRefund._id } : {},
                          { withCredentials: true }
                        );
                        toast.success("Return received confirmed. Refund credited to buyer wallet.");
                        await dispatch(getAllOrdersOfShop(sellerId));
                      } catch (error) {
                        toast.error(error?.response?.data?.message || "Failed to confirm return receipt.");
                      }
                    }}
                    className="inline-flex h-11 items-center justify-center rounded-xl bg-green-600 px-6 text-sm font-semibold text-white transition hover:bg-green-700"
                  >
                    Confirm return received
                  </button>
                )}
                <button
                  type="button"
                  onClick={openChatWithBuyer}
                  className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-6 text-sm font-semibold text-slate-700 transition hover:border-[#38513b] hover:text-[#38513b]"
                >
                  <BiMessageSquareDetail size={18} />
                  Contact Buyer via Inbox
                </button>
                <Link
                  to={`/profile/seller-order/${order._id}`}
                  className="inline-flex h-11 items-center justify-center rounded-xl border border-slate-200 bg-white px-6 text-sm font-semibold text-slate-700 transition hover:border-[#38513b] hover:text-[#38513b]"
                >
                  View Full Order Details
                </Link>
              </div>
            </section>
          )}

          {partialModal.open && (
            <MobileFriendlyModal
              onClose={() => setPartialModal({ open: false, refundId: null, amount: "" })}
            >
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Send partial refund offer</h3>
              <p className="text-sm text-slate-600 mb-4">
                Enter the amount to offer the buyer (item value only; max is the line refund cap). The buyer must accept
                before it is credited to their wallet. VA fees are not refunded.
              </p>
              <label className="block text-xs font-medium text-slate-600 mb-1">Amount (£)</label>
              <input
                type="number"
                min="0.01"
                step="0.01"
                inputMode="decimal"
                value={partialModal.amount}
                onChange={(e) => setPartialModal((prev) => ({ ...prev, amount: e.target.value }))}
                className={MODAL_INPUT_CLASS}
              />
              <div className="mt-4 flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => setPartialModal({ open: false, refundId: null, amount: "" })}
                  className="inline-flex h-10 items-center justify-center rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const raw = parseFloat(String(partialModal.amount).replace(",", "."));
                    if (!Number.isFinite(raw) || raw <= 0) {
                      toast.error("Enter a valid amount greater than zero.");
                      return;
                    }
                    handleRefundAction("Refund Success", partialModal.refundId, "", {
                      sellerSettlement: "wallet_partial_now",
                      partialAmount: raw,
                    });
                  }}
                  disabled={isProcessing}
                  className="inline-flex h-10 items-center justify-center rounded-xl bg-teal-600 px-4 text-sm font-semibold text-white hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isProcessing ? "Processing…" : "Send offer"}
                </button>
              </div>
            </MobileFriendlyModal>
          )}

        </div>
      </div>
    </div>
  );
};

export default RefundDetails;

